import React from 'react';
import isEmpty from 'lodash/isEmpty';
/**
 * Render a tip tap JSON node and all its children
 * @param { TipTapBaseNode} node JSON node to render
 * @param { TipTapNodeHandlers} handlers a handler for each node type
 * @returns tree of components as react elements
 */ export function TipTapRender(props) {
    const { node, handlers: mapping } = props;
    node.externalRenderers = props.externalRenderers;
    node.extensionConfig = props.extensionConfig;
    node.containerWidth = props.containerWidth;
    node.presetId = props.presetId;
    // recursively render child content
    const children = [];
    // Find link marks, they need special handling because
    // anchors are not simple attributes on an <a> element
    // they are infact wrapping elements that have rules.
    // This deal with other marks breaking the link marks
    // into separate marks and ending up with say three links
    // instead of one when you have <a>a<strong>b</strong>c<a/>
    // without this that would end up as <a>a</a><a>b</a><a>c</a>
    //
    // Logic here is to find the links and then rewrite the content
    // so we have a nested link structure, this works as anchors cannot
    // appear inside each other meaning we can convert:
    // [1]
    // [2]
    // [3]
    // [4]
    // Into:
    // [1]
    //   [2][3]
    // [4]
    // Where 1 becomes the linkwrapper and all the parts with the same
    // href get pushed into its children.
    const anchorsByHref = {}; // map of href
    let startIndex = 0; // tracks the start of each link, deals with dupes
    const indexToHref = []; // used to track which children have been replaced later on
    node.content && node.content.forEach((child, index)=>{
        var _child_marks, _child_marks1;
        if (!((_child_marks = child.marks) === null || _child_marks === void 0 ? void 0 : _child_marks.find)) {
            return;
        }
        const link = (_child_marks1 = child.marks) === null || _child_marks1 === void 0 ? void 0 : _child_marks1.find((m)=>m.type === 'link');
        if (link && link.attrs) {
            var _child_marks2;
            // Make a uniqueID for the link, handling links with same href in the same block
            const hrefId = `${link.attrs.href}:${startIndex}:${index}`;
            if (!anchorsByHref[hrefId]) {
                anchorsByHref[hrefId] = {
                    children: []
                };
            }
            // Filters out the link marks as we are hoisting it up a level
            child.marks = (_child_marks2 = child.marks) === null || _child_marks2 === void 0 ? void 0 : _child_marks2.filter((m)=>m.type !== 'link');
            anchorsByHref[hrefId].link = link;
            anchorsByHref[hrefId].children.push(child); // we collect the children
            anchorsByHref[hrefId].index = index;
            var _anchorsByHref_hrefId_startIndex;
            anchorsByHref[hrefId].startIndex = (_anchorsByHref_hrefId_startIndex = anchorsByHref[hrefId].startIndex) !== null && _anchorsByHref_hrefId_startIndex !== void 0 ? _anchorsByHref_hrefId_startIndex : index;
            indexToHref[index] = hrefId;
        } else {
            // when we don't have a link, bumped the startIndex as the links
            // span over the text has finished and if there is another of the
            // same href we can handle that uniquely
            startIndex = index;
        }
    });
    if (!isEmpty(anchorsByHref) && node.content) {
        // Here we rewrite the node.content so it include link wrapper with nested
        // children rather than a flat array.
        // We want to maintain the index order here, so we are pushing non links
        // etc so we don't end up with just the links and no other content.
        const newContent = [];
        node.content.forEach((child, index)=>{
            // Not a link
            if (indexToHref[index] === undefined) {
                newContent.push(child);
                return;
            }
            // Ignore hrefs we have already wrapped the children are already added
            // this skips nodes that we have pushed into the link group above in anchorsByHref
            // like: ['href1', 'href1', 'href1'] -> [false, 'href1', 'href1']  -> [false, false, 'href1']
            if (index > 0 && indexToHref[index - 1] === false) {
                indexToHref[index] = false;
                return;
            }
            // Adds special wrapper than renders nothing but nested the correct children
            // and tells the mark to render a link.
            if (indexToHref[index]) {
                const grouped = anchorsByHref[indexToHref[index]];
                const linkSpan = {
                    type: 'link-wrapper',
                    marks: [
                        grouped.link
                    ],
                    content: grouped.children
                };
                newContent.push(linkSpan);
                indexToHref[index] = false;
            }
        });
        node.content = newContent;
    }
    node.content && node.content.forEach((child, ix)=>{
        children.push(React.createElement(TipTapRender, {
            node: child,
            handlers: mapping,
            key: `${child.type}-${ix}`,
            externalRenderers: props.externalRenderers,
            extensionConfig: props.extensionConfig,
            containerWidth: props.containerWidth,
            presetId: props.presetId
        }));
    });
    const nodeType = node.type;
    // return empty if we are missing a handler for this type
    if (!(nodeType in props.handlers)) {
        // Temp ones never render
        if ([
            'da-muro',
            'da-dream'
        ].includes(nodeType)) {
            return React.createElement(React.Fragment, null);
        }
        // eslint-disable-next-line no-console
        console.info(`tiptap renderer, missing node type`, nodeType, node);
        return React.createElement(React.Fragment, null);
    }
    let renderedElement = null;
    if (nodeType === 'text') {
        renderedElement = React.createElement(mapping[nodeType], {
            node: node
        }, children);
    } else if (nodeType === 'heading') {
        renderedElement = React.createElement(mapping[nodeType], {
            node: node
        }, children);
    } else if (nodeType === 'codeBlock') {
        renderedElement = React.createElement(mapping[nodeType], {
            node: node
        }, children);
    } else if (nodeType === 'paragraph' && !node.content) {
        // special case for empty paragraphs, make it hard break
        renderedElement = React.createElement(mapping['hardBreak'], {
            node: node
        }, children);
    } else {
        renderedElement = React.createElement(mapping[nodeType], {
            node: node
        }, children);
    }
    // now wrap all the marks
    if (node.marks && node.marks.forEach) {
        node.marks.forEach((mark)=>{
            const markType = mark.type;
            if (markType in props.handlers) {
                switch(markType){
                    case 'link':
                        renderedElement = React.createElement(mapping[markType], {
                            node: mark
                        }, renderedElement);
                        break;
                    default:
                        renderedElement = React.createElement(mapping[markType], {
                            node: mark
                        }, renderedElement);
                        break;
                }
            } else {
                // eslint-disable-next-line no-console
                console.info(`tiptap renderer, missing mark type`, markType, mark);
            }
        });
    }
    return renderedElement;
}

//# sourceMappingURL=renderer.js.map